import moment from 'moment'
import { db, FieldValue } from '@/utils/Firebase'

export const HABIT_CATEGORIES = ['hydration', 'nutrition', 'sleep', 'activity', 'recovery', 'supplements', 'mental']

export const HABIT_CATEGORY_ICONS = {
    hydration: 'mdi-water-outline',
    nutrition: 'mdi-leaf',
    sleep: 'mdi-weather-night',
    activity: 'mdi-walk',
    recovery: 'mdi-sync',
    supplements: 'mdi-pill',
    mental: 'mdi-emoticon-happy-outline',
    custom: 'mdi-pencil-outline',
}

export const HABIT_TYPES = ['bool', 'qty', 'time']

export const HABIT_UNITS = ['liters', 'grams', 'portions', 'hours', 'steps', 'times', 'min']

export const HABIT_CATALOG = [
    { id: 'water', category: 'hydration', type: 'qty', goal: 2.5, unit: 'liters', hours: [10, 14, 18] },
    { id: 'no_sugary_drinks', category: 'hydration', type: 'bool' },
    { id: 'no_alcohol', category: 'hydration', type: 'bool' },
    { id: 'protein', category: 'nutrition', type: 'qty', goal: 120, unit: 'grams' },
    { id: 'vegetables', category: 'nutrition', type: 'qty', goal: 3, unit: 'portions' },
    { id: 'meal_plan', category: 'nutrition', type: 'bool' },
    { id: 'log_meals', category: 'nutrition', type: 'bool', hours: [13, 21] },
    { id: 'sleep_hours', category: 'sleep', type: 'qty', goal: 7.5, unit: 'hours', hours: [8] },
    { id: 'bed_before_23', category: 'sleep', type: 'bool', hours: [22] },
    { id: 'no_screens', category: 'sleep', type: 'time', goal: 30, unit: 'min', hours: [22] },
    { id: 'steps', category: 'activity', type: 'qty', goal: 8000, unit: 'steps', hours: [18] },
    { id: 'light_cardio', category: 'activity', type: 'time', goal: 30, unit: 'min', frequency: { kind: 'weekly', times: 3 } },
    { id: 'active_breaks', category: 'activity', type: 'qty', goal: 4, unit: 'times', hours: [11, 16] },
    { id: 'mobility', category: 'recovery', type: 'time', goal: 15, unit: 'min' },
    { id: 'prevention', category: 'recovery', type: 'bool', frequency: { kind: 'weekly', times: 3 } },
    { id: 'foam_roller', category: 'recovery', type: 'time', goal: 10, unit: 'min' },
    { id: 'creatine', category: 'supplements', type: 'bool', hours: [9] },
    { id: 'protein_powder', category: 'supplements', type: 'bool', hours: [17] },
    { id: 'custom_supplement', category: 'supplements', type: 'bool', hours: [9] },
    { id: 'meditation', category: 'mental', type: 'time', goal: 10, unit: 'min', hours: [21] },
    { id: 'visualization', category: 'mental', type: 'bool', hours: [20] },
    { id: 'journaling', category: 'mental', type: 'bool', hours: [22] },
]

export const QUICK_ADD = {
    liters: [0.25, 0.5, 1],
    hours: [0.5, 1],
    min: [5, 10, 15],
}

export const DATE_FORMAT = 'YYYY-MM-DD'
export const EDITABLE_DAYS = 7
export const ADHERENCE_GOOD = 75
export const ADHERENCE_MID = 50

export const newHabitKey = () => `h${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export const habitFromCatalog = (base) => ({
    key: newHabitKey(),
    baseId: base.id,
    name: null,
    category: base.category,
    type: base.type,
    goal: base.goal ?? null,
    unit: base.type === 'time' ? 'min' : (base.unit || null),
    frequency: base.frequency ? { ...base.frequency } : { kind: 'daily' },
    alarm: { enabled: false, hours: [...(base.hours || [20])] },
})

export const customHabit = () => ({
    key: newHabitKey(),
    baseId: null,
    name: '',
    category: 'custom',
    type: 'bool',
    goal: null,
    unit: null,
    frequency: { kind: 'daily' },
    alarm: { enabled: false, hours: [20] },
})

export const emptyCombo = () => ({
    name: '',
    folder: null,
    duration: 4,
    notes: '',
    tags: [],
    alarms: { mode: 'perHabit', dailyHour: 20, closing: { enabled: false, hour: 21 } },
    habits: [],
})

export const frequencyValue = (frequency) => {
    if (!frequency || frequency.kind === 'daily') return 'daily'
    if (frequency.kind === 'weekly') return `weekly_${frequency.times}`
    return `md_${frequency.offset}`
}

export const frequencyFromValue = (value) => {
    if (value === 'daily') return { kind: 'daily' }
    const [kind, n] = value.split('_')
    if (kind === 'weekly') return { kind: 'weekly', times: Number(n) }
    return { kind: 'md', offset: Number(n) }
}

export const FREQUENCY_VALUES = ['daily', 'weekly_2', 'weekly_3', 'weekly_4', 'weekly_5', 'weekly_6', 'md_-1', 'md_0', 'md_1']

export const isMdFrequency = (frequency) => frequency?.kind === 'md'

export const mdLabel = (offset) => offset === 0 ? 'MD' : `MD${offset > 0 ? '+' : ''}${offset}`

export const habitGoal = (habit, goals = {}) => {
    const override = goals?.[habit.key]
    if (override !== undefined && override !== null && override !== '') return Number(override)
    return habit.goal === null || habit.goal === undefined ? null : Number(habit.goal)
}

export const habitScore = (habit, value, goals) => {
    if (value === undefined || value === null) return 0
    if (habit.type === 'bool') return value === true ? 1 : 0
    const goal = habitGoal(habit, goals)
    if (!goal) return Number(value) > 0 ? 1 : 0
    return Math.min(1, Number(value) / goal)
}

export const habitDone = (habit, value, goals) => habitScore(habit, value, goals) >= 1

export const weekId = (date) => {
    const m = moment(date, DATE_FORMAT)
    return `${m.isoWeekYear()}-W${String(m.isoWeek()).padStart(2, '0')}`
}

export const weekStartOf = (date) => moment(date, DATE_FORMAT).startOf('isoWeek').format(DATE_FORMAT)

export const weekDocId = (studentId, date) => `${studentId}_${weekId(date)}`

export const weekDates = (weekStart) => Array.from({ length: 7 }, (_, i) => moment(weekStart, DATE_FORMAT).add(i, 'days').format(DATE_FORMAT))

export const today = () => moment().format(DATE_FORMAT)

export const isAssignmentActive = (assignment, date = today()) => {
    if (!assignment?.combo) return false
    if (assignment.startDate && date < assignment.startDate) return false
    if (assignment.endDate && date > assignment.endDate) return false
    return true
}

export const mdMapFromCompetences = (dates = []) => {
    const map = {}
    dates.forEach(date => {
        [-1, 0, 1].forEach(offset => {
            const day = moment(date, DATE_FORMAT).add(offset, 'days').format(DATE_FORMAT)
            map[day] = [...new Set([...(map[day] || []), offset])]
        })
    })
    return map
}

export const habitAppliesOn = (habit, date, mdMap = {}) => {
    const kind = habit.frequency?.kind || 'daily'
    if (kind !== 'md') return true
    return (mdMap[date] || []).includes(habit.frequency.offset)
}

export const weeklyDoneCount = (habit, weekDays = {}, goals, untilDate = null) => {
    return Object.keys(weekDays).filter(date => (!untilDate || date <= untilDate)
        && habitDone(habit, weekDays[date]?.values?.[habit.key]?.v, goals)).length
}

export const dayRequestedHabits = (habits, date, dayEntry, mdMap, goals) => {
    return habits.filter(habit => {
        if (!habitAppliesOn(habit, date, mdMap)) return false
        if (habit.frequency?.kind !== 'weekly') return true
        return habitDone(habit, dayEntry?.values?.[habit.key]?.v, goals)
    })
}

export const dayProgress = (habits, date, dayEntry, mdMap, goals) => {
    const requested = dayRequestedHabits(habits, date, dayEntry, mdMap, goals)
    const done = requested.filter(habit => habitDone(habit, dayEntry?.values?.[habit.key]?.v, goals)).length
    const score = requested.length
        ? requested.reduce((acc, habit) => acc + habitScore(habit, dayEntry?.values?.[habit.key]?.v, goals), 0) / requested.length
        : null
    return { total: requested.length, done, score }
}

export const dayStatus = (progress) => {
    if (!progress || !progress.total) return 'none'
    if (progress.done >= progress.total) return 'full'
    if (progress.score > 0) return 'partial'
    return 'empty'
}

export const computeWeekSummary = ({ habits, goals, days = {}, mdDays = {}, weekStart, startDate, endDate, until }) => {
    const dates = weekDates(weekStart).filter(date => (!startDate || date >= startDate)
        && (!endDate || date <= endDate) && (!until || date <= until))
    const perHabit = {}
    const perDay = {}
    const hourHistogram = Array(24).fill(0)

    habits.forEach(habit => {
        const kind = habit.frequency?.kind || 'daily'
        let requested = 0
        let done = 0
        let scoreSum = 0
        if (kind === 'weekly') {
            requested = habit.frequency.times
            done = dates.filter(date => habitDone(habit, days[date]?.values?.[habit.key]?.v, goals)).length
            scoreSum = Math.min(done, requested)
        } else {
            dates.forEach(date => {
                if (!habitAppliesOn(habit, date, mdDays)) return
                requested++
                const value = days[date]?.values?.[habit.key]?.v
                if (habitDone(habit, value, goals)) done++
                scoreSum += habitScore(habit, value, goals)
            })
        }
        if (!requested) return
        perHabit[habit.key] = { done, requested, adherence: Math.round(Math.min(1, scoreSum / requested) * 100) }
    })

    dates.forEach(date => {
        const progress = dayProgress(habits, date, days[date], mdDays, goals)
        if (progress.total) perDay[date] = Math.round(progress.score * 100)
        Object.values(days[date]?.values || {}).forEach(entry => {
            if (!entry?.at) return
            const at = entry.at.toDate ? entry.at.toDate() : new Date(entry.at)
            hourHistogram[at.getHours()]++
        })
    })

    const values = Object.values(perHabit).map(item => item.adherence)
    const adherence = values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null
    return { adherence, perHabit, perDay, hourHistogram }
}

export const computeStreak = (habits, goals, daysByDate, mdMap, startDate, until = today()) => {
    let count = 0
    let cursor = moment(until, DATE_FORMAT)
    const todayProgress = dayProgress(habits, until, daysByDate[until], mdMap, goals)
    if (todayProgress.total && todayProgress.done < todayProgress.total) cursor.subtract(1, 'days')
    for (let i = 0; i < 400; i++) {
        const date = cursor.format(DATE_FORMAT)
        if (startDate && date < startDate) break
        if (!(date in daysByDate) && moment(until, DATE_FORMAT).diff(cursor, 'days') > 34) break
        const progress = dayProgress(habits, date, daysByDate[date], mdMap, goals)
        if (progress.total) {
            if (progress.done < progress.total) break
            count++
        }
        cursor.subtract(1, 'days')
    }
    return count
}

export const resolveAlarmHours = (assignment) => {
    const combo = assignment?.combo
    if (!combo) return []
    const prefs = assignment.studentPrefs || {}
    if (prefs.singleReminder) return [Number(prefs.reminderHour ?? combo.alarms?.dailyHour ?? 20)]
    const override = assignment.alarmsOverride || null
    if (override === 'none') return []
    if (override === 'daily' || combo.alarms?.mode === 'daily') return [Number(combo.alarms?.dailyHour ?? 20)]
    const hours = new Set()
    combo.habits.forEach(habit => {
        if (habit.alarm?.enabled) (habit.alarm.hours || []).forEach(hour => hours.add(Number(hour)))
    })
    if (combo.alarms?.closing?.enabled) hours.add(Number(combo.alarms.closing.hour))
    return [...hours].sort((a, b) => a - b)
}

export const toUtcHours = (hours, utcOffset = moment().utcOffset()) => {
    const shift = Math.round(utcOffset / 60)
    return [...new Set(hours.map(hour => ((hour - shift) % 24 + 24) % 24))].sort((a, b) => a - b)
}

export const pushSchedule = (assignment, utcOffset = moment().utcOffset()) => ({
    utcOffset,
    pushHoursUtc: toUtcHours(resolveAlarmHours(assignment), utcOffset),
})

export const syncHabitTimezone = async (studentId) => {
    if (!studentId) return
    const ref = db.collection('habitAssignments').doc(studentId)
    const snap = await ref.get()
    if (!snap.exists) return
    const assignment = snap.data()
    const offset = moment().utcOffset()
    if (assignment.utcOffset === offset) return
    if (assignment.endDate && assignment.endDate < today()) return
    await ref.update(pushSchedule(assignment, offset))
}

export const comboSnapshot = (combo) => ({
    name: combo.name || '',
    notes: combo.notes || '',
    alarms: JSON.parse(JSON.stringify(combo.alarms || emptyCombo().alarms)),
    habits: JSON.parse(JSON.stringify(combo.habits || [])),
})

export const assignCombo = async ({ combo, comboId, coachId, studentIds, startDate, weeks, alarmsOverride }) => {
    const endDate = weeks ? moment(startDate, DATE_FORMAT).add(weeks * 7 - 1, 'days').format(DATE_FORMAT) : null
    const ids = [...new Set(studentIds.filter(Boolean))]
    const snapshot = comboSnapshot(combo)
    const existing = await Promise.all(ids.map(id => db.collection('habitAssignments').doc(id).get()))
    const batch = db.batch()
    ids.forEach((studentId, i) => {
        const previous = existing[i].exists ? existing[i].data() : null
        const payload = {
            studentId,
            coachId,
            comboId,
            combo: snapshot,
            startDate,
            endDate,
            alarmsOverride: alarmsOverride || null,
            goals: {},
            studentPrefs: previous?.studentPrefs || { singleReminder: false, reminderHour: snapshot.alarms.dailyHour },
            streak: { count: 0, best: previous?.streak?.best || 0 },
            assignedAt: FieldValue.serverTimestamp(),
        }
        const utcOffset = previous?.utcOffset ?? moment().utcOffset()
        batch.set(db.collection('habitAssignments').doc(studentId), { ...payload, ...pushSchedule(payload, utcOffset) })
    })
    batch.update(db.collection('habitCombos').doc(comboId), { used: FieldValue.increment(ids.length) })
    await batch.commit()
    return ids.length
}

export const loadWeeks = async (studentId, fromDate, toDate = today()) => {
    const ids = []
    let cursor = moment(weekStartOf(fromDate), DATE_FORMAT)
    while (cursor.format(DATE_FORMAT) <= toDate) {
        ids.push(weekDocId(studentId, cursor.format(DATE_FORMAT)))
        cursor.add(7, 'days')
    }
    const snaps = await Promise.all(ids.map(id => db.collection('habitWeeks').doc(id).get()))
    return snaps.filter(snap => snap.exists).map(snap => ({ id: snap.id, ...snap.data() }))
}

export const saveHabitValue = async ({ assignment, studentId, date, habitKey, value, days, mdMap }) => {
    const weekStart = weekStartOf(date)
    const ref = db.collection('habitWeeks').doc(weekDocId(studentId, date))
    const habits = assignment.combo.habits
    const goals = assignment.goals || {}
    const mdDays = {}
    weekDates(weekStart).forEach(day => { if (mdMap[day]) mdDays[day] = mdMap[day] })
    const weekDays = {}
    weekDates(weekStart).forEach(day => { if (days[day]) weekDays[day] = days[day] })
    const summary = computeWeekSummary({
        habits, goals, days: weekDays, mdDays, weekStart,
        startDate: assignment.startDate, endDate: assignment.endDate, until: today(),
    })
    const entry = value === null ? FieldValue.delete() : { v: value, at: FieldValue.serverTimestamp() }
    const base = {
        studentId,
        coachId: assignment.coachId,
        comboId: assignment.comboId,
        comboName: assignment.combo.name,
        weekStart,
        habits,
        goals,
        mdDays,
        summary,
        updatedAt: FieldValue.serverTimestamp(),
    }
    await ref.set({ ...base, days: { [date]: { values: { [habitKey]: entry } } } }, { merge: true })
}

export const daysFromWeeks =(weeks) => weeks.reduce((acc, week) => ({ ...acc, ...(week.days || {}) }), {})

export const loadCompetenceDates = async (studentId, fromDate, toDate) => {
    const snap = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId)
        .where('type', '==', 'competence')
        .where('rawDate.full', '>=', fromDate)
        .where('rawDate.full', '<=', toDate)
        .get()
    return [...new Set(snap.docs.map(doc => doc.data()?.rawDate?.full).filter(Boolean))]
}

export const chunk = (list, size = 10) => {
    const out = []
    for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
    return out
}

export const adherenceLevel = (value) => {
    if (value === null || value === undefined) return 'none'
    if (value >= ADHERENCE_GOOD) return 'good'
    if (value >= ADHERENCE_MID) return 'mid'
    return 'bad'
}
